/**
 * Ultra-Modern DataTable Component
 * Professional Admin Portal Design
 */

import React from 'react';
import { MdEdit, MdDelete } from 'react-icons/md';
import { cn } from '../../lib/utils';

interface Column<T> {
  key: string;
  header: string | React.ReactNode;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  actionsLabel?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  rowClassName?: string;
  compact?: boolean;
  footer?: React.ReactNode;
}

export function DataTable<T extends { _id?: string }>({
  columns,
  data,
  onEdit,
  onDelete,
  onRowClick,
  emptyMessage = 'No data found',
  actionsLabel = 'Actions',
  canEdit = true,
  canDelete = true,
  rowClassName,
  compact = false,
  footer,
}: DataTableProps<T>) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="p-16 text-center rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <p className="text-lg font-semibold text-[hsl(var(--muted-foreground))]">{emptyMessage}</p>
      </div>
    );
  }

  const cellPad = compact ? 'px-4 py-3' : 'px-6 py-4';
  const showActions = (onEdit && canEdit) || (onDelete && canDelete);

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className={cn('w-full border-collapse', compact && 'table-fixed')}>
          <thead>
            <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    cellPad,
                    'text-left text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wide align-middle',
                    column.className
                  )}
                >
                  {typeof column.header === 'string' ? column.header : column.header}
                </th>
              ))}
              {showActions && (
                <th className={cn(cellPad, 'text-xs font-semibold text-[hsl(var(--foreground))] uppercase tracking-wide text-center align-middle', compact ? 'w-24' : 'w-32')}>
                  {actionsLabel}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--border))]">
            {data.map((item, index) => (
              <tr
                key={item._id || index}
                onClick={() => onRowClick?.(item)}
                className={cn(
                  'hover:bg-[hsl(var(--muted))] transition-colors group',
                  onRowClick && 'cursor-pointer',
                  rowClassName
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      cellPad,
                      'text-sm text-[hsl(var(--foreground))] align-middle',
                      column.className
                    )}
                  >
                    {column.render ? column.render(item) : String((item as any)[column.key])}
                  </td>
                ))}
                {showActions && (
                  <td className={cn(cellPad, 'text-center align-middle', compact && 'w-24')}>
                    <div className="inline-flex items-center justify-center gap-2">
                      {onEdit && canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(item);
                          }}
                          className="p-2 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20 hover:bg-[hsl(var(--primary))]/20 transition-colors"
                          aria-label="Edit"
                        >
                          <MdEdit className="w-4 h-4" />
                        </button>
                      )}
                      {onDelete && canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item);
                          }}
                          className="p-2 rounded-lg bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/20 hover:bg-[hsl(var(--destructive))]/20 transition-colors"
                          aria-label="Delete"
                        >
                          <MdDelete className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

